import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMunicipalities } from '../../hooks/geography'
import { CustomAutocomplete, generateFilterGraphql } from '../..'
import { IFormField, Municipality } from '../../models'
import { Autocomplete, AutocompleteChangeReason, TextField } from '@mui/material'

export interface MunicipalitySelectProps extends IFormField {
  label?: string
  provinceId: string
  onChangeMunicipality: (province: Municipality) => void
  disabled?: boolean
  defaultValue?: string
}

export const MunicipalitySelect = ({
  provinceId,
  onChangeMunicipality,
  disabled,
  defaultValue,
  ...props
}: MunicipalitySelectProps) => {
  const {
    form: { setFieldValue, values, errors },
    field: { name },
  } = props

  const { getMunicipalities, results, loading } = useMunicipalities()
  const { t: transLabel } = useTranslation()
  const municipalities = results.results
  const [selected, setSelected] = useState<any>(null)
  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    if (provinceId !== '') {
      filterMunicipalities(provinceId)
    }
  }, [provinceId])

  useEffect(() => {
    if (municipalities.length > 0 && defaultValue && !selected) {
      const filter = municipalities.filter((item: any) => item.id === defaultValue)
      if (filter.length > 0) {
        setSelected(filter[0])
        setFieldValue(name, filter[0])
      }
    }
  }, [defaultValue, municipalities])

  const filterMunicipalities = (value: string, skip = 0, take = 100) => {
    const customFilters = {
      and: [{ stateOrProvinceId: { ...generateFilterGraphql(value, 'eq') } }],
    }

    getMunicipalities({ where: customFilters, skip, take })
  }

  const handleFilter = () => {}

  const handleChange = useCallback(
    (event: React.ChangeEvent<any>, value: any, reason: AutocompleteChangeReason) => {
      setFieldValue(name, value)
      setSelected(value)

      if (onChangeMunicipality instanceof Function) {
        onChangeMunicipality(value)
      }
    },
    [setFieldValue, name, onChangeMunicipality],
  )

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setInputValue(value)
  }

  return (
    <Autocomplete
      sx={{
        m: 0,
        minWidth: 'inherit',
      }}
      onChange={handleChange}
      value={selected}
      getOptionLabel={(option) => (option['name'] ? option['name'] : '')}
      options={municipalities}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          onChange={onInputChange}
          label={transLabel('CIUDAD')}
          variant="outlined"
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
          error={!!errors[name]}
          helperText={errors[name] ?? ''}
          autoComplete={'off'}
        />
      )}
      disabled={disabled}
      {...props}
    />
  )
}
